<template>
  <div :disabled="!isOnboarding">
    <v-row>
      <h2 class="pr-4 mb-2 d-inline">{{ $t('labels.billing-data.billing-info.title') }}</h2>
    </v-row>
    <v-row class="d-flex justify-space-between">
      <v-col
        cols="12"
        md="6"
        class="pr-0 pa-0 pr-md-2"
      >
        <v-text-field
          ref="firstname"
          v-model.lazy="localData.firstname"
          variant="outlined"
          :label="$t('labels.first-name')"
          :placeholder="$t('labels.billing-data.placeholders.first-name')"
        />
      </v-col>
      <v-col
        cols="12"
        md="6"
        class="pa-0"
      >
        <v-text-field
          ref="lastname"
          v-model.lazy="localData.lastname"
          variant="outlined"
          :label="$t('labels.last-name')"
          :placeholder="$t('labels.billing-data.placeholders.last-name')"
        />
      </v-col>
    </v-row>
    <v-row class="d-flex justify-space-between">
      <v-col
        cols="12"
        md="6"
        class="pr-0 pa-0 pr-md-2"
      >
        <PhoneField
          v-model="localData.phone"
          :disabled="!isOnboarding"
        />
      </v-col>
      <v-col
        cols="12"
        md="6"
        class="pa-0"
      >
        <v-text-field
          ref="email"
          v-model.lazy="localData.email"
          type="email"
          variant="outlined"
          :label="$t('labels.email')"
          :placeholder="$t('labels.billing-data.placeholders.email')"
          :rules="[rules.email]"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-text-field
        ref="companyName"
        v-model.lazy="companyName"
        variant="outlined"
        :counter="companyName.length > 90"
        :label="$t('labels.billing-data.billing-info.company-name')"
        :placeholder="companyName || $t('labels.billing-data.placeholders.company-name')"
        :rules="[rules.length({ max: 100 })]"
      />
    </v-row>
    <v-row>
      <v-text-field
        v-if="showCompanyNameAddition"
        ref="companyNameAddition"
        v-model.lazy="localData.companyNameAddition"
        variant="outlined"
        :label="$t('labels.billing-data.billing-info.company-name-addition')"
        :placeholder="$t('labels.billing-data.placeholders.company-name')"
        :rules="[rules.length({ max: 50 })]"
      />
    </v-row>
    <v-row>
      <v-col
        cols="12"
        md="3"
        class="pa-0 mr"
      >
        <v-text-field
          ref="vatId"
          v-model.lazy="localData.vatId"
          variant="outlined"
          :label="$t('labels.billing-data.billing-info.vat-id')"
          :placeholder="placeholder.vatId"
          :rules="localData.noVatIdAvailable || localData.vatIdRequested ? [] : [rules.vatId]"
          :disabled="localData.vatIdRequested || localData.noVatIdAvailable"
          @update:model-value="onVatIdInput"
        />
      </v-col>
      <v-col
        cols="12"
        md="8"
        class="pl-6 mt-0 pa-0"
      >
        <v-checkbox
          ref="vatIdRequested"
          v-model="localData.vatIdRequested"
          class="pr-4 mt-0"
          :disabled="!isOnboarding || localData.noVatIdAvailable"
          outlined
          :label="$t('labels.billing-data.billing-info.vat-id-requested')"
          @update:model-value="onVatIdRequestedToggle"
        />
      </v-col>
      <v-col
        v-if="localData.vatIdRequested"
        cols="4"
        md="2"
        class="pa-0"
      >
        <v-menu
          v-model="vatIdRequestDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
        >
          <template v-slot:activator="{ props }">
            <v-text-field
              v-model="vatIdRequestDate"
              :label="$t('labels.billing-data.billing-info.vat-id-request-date')"
              variant="outlined"
              readonly
              v-bind="props"
            />
          </template>
          <v-date-picker
            v-model="vatIdRequestDate"
            :max="getEndDate"
            color="primary"
            :rules="localData.vatIdRequested ? [rules.required] : []"
            @change="vatIdRequestDateMenu = false"
          />
        </v-menu>
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
        class="pa-0"
      >
        <v-checkbox
          ref="noVatIdAvailable"
          v-model="localData.noVatIdAvailable"
          class="mt-0 mb-0"
          color="primary"
          hide-details
          :disabled="!isOnboarding || localData.vatIdRequested"
          :label="$t('labels.no-vat-id-available')"
          @update:model-value="onNoVatIdToggle"
        />
        <v-text-field
          v-if="localData.noVatIdAvailable"
          ref="taxNumber"
          v-model.lazy="localData.taxNumber"
          class="mt-3"
          variant="outlined"
          :label="$t('labels.tax-number')"
          :disabled="!isOnboarding"
        />
      </v-col>
    </v-row>
    <v-row>
      <h2 class="pr-4 d-inline">{{ $t('labels.billing-data.billing-address.title') }}</h2>
    </v-row>
    <v-row class="mt-1">
      <v-checkbox
        v-model="localData.hasSeparateBillingAddress"
        :disabled="!isOnboarding"
      >
        <template v-slot:label>
          <span class="text-h6 font-weight-bold">{{
            $t('labels.billing-data.billing-address.separate-address')
          }}</span>
        </template>
      </v-checkbox>
    </v-row>
    <v-row>
      <v-text-field
        ref="street"
        v-model.lazy="billingAddress.street"
        :disabled="!localData.hasSeparateBillingAddress"
        variant="outlined"
        :label="$t('labels.billing-data.billing-address.street')"
        :placeholder="localData.companyStreet || $t('labels.billing-data.placeholders.street')"
        :rules="localData.hasSeparateBillingAddress ? [rules.required] : []"
        @update:model-value="updateBillingAddress()"
      />
    </v-row>
    <v-row class="mb-6 d-flex justify-space-between">
      <v-col
        cols="12"
        md="4"
        class="pr-0 pa-0 pr-md-2"
      >
        <v-text-field
          ref="street"
          v-model.lazy="billingAddress.zip"
          :disabled="!localData.hasSeparateBillingAddress"
          variant="outlined"
          type="number"
          :label="$t('labels.billing-data.billing-address.zip')"
          :placeholder="localData.companyZip || $t('labels.billing-data.placeholders.zip')"
          :rules="[
            rules.length({ min: 4, max: 5 }),
            rules.zip,
            ...(localData.hasSeparateBillingAddress ? [rules.required] : []),
          ]"
          @update:model-value="updateBillingAddress()"
        />
      </v-col>
      <v-col
        cols="12"
        md="8"
        class="pa-0"
      >
        <v-text-field
          ref="city"
          v-model.lazy="billingAddress.city"
          :disabled="!localData.hasSeparateBillingAddress"
          variant="outlined"
          :label="$t('labels.billing-data.billing-address.city')"
          :placeholder="localData.companyCity || $t('labels.billing-data.placeholders.city')"
          :rules="localData.hasSeparateBillingAddress ? [rules.required] : []"
          @update:model-value="updateBillingAddress()"
        />
      </v-col>
    </v-row>
    <v-row>
      <h2 class="pr-4 d-inline">{{ $t('labels.billing-data.billing.title') }}</h2>
    </v-row>
    <v-row>
      <v-radio-group
        v-model="localData.paymentMethod"
        inline
      >
        <v-radio
          v-for="{ value, name: label } in paymentMethods"
          :key="value"
          :label="label"
          :value="value"
          disabled
        />
      </v-radio-group>
    </v-row>
    <v-row
      v-if="localData.companyCountry !== 'ch' && localData.paymentMethod === 'DIRECT_DEBIT'"
      class="d-flex justify-space-between"
    >
      <v-col
        cols="12"
        md="8"
        class="pr-0 pa-0 pr-md-2"
      >
        <v-text-field
          ref="iban"
          v-model.lazy="localData.iban"
          variant="outlined"
          :label="$t('labels.billing-data.billing.iban')"
          :disabled="!isOnboarding"
          :placeholder="placeholder.iban"
          :rules="[rules.iban]"
        />
      </v-col>
      <v-col
        cols="12"
        md="4"
        class="pa-0"
      >
        <v-text-field
          ref="bic"
          v-model.lazy="localData.bic"
          variant="outlined"
          :label="$t('labels.billing-data.billing.bic')"
          :disabled="!isOnboarding"
          :placeholder="$t('labels.billing-data.placeholders.bic')"
          :rules="[rules.bic]"
        />
      </v-col>
      <v-col
        cols="12"
        class="pa-0"
      >
        <h4 class="pb-4">
          {{ $t('labels.billing-data.billing.sepa-info') }}
        </h4>
        <div class="pb-4">
          {{ $t('labels.billing-data.billing.sepa-download-text') }}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://s3.eu-central-1.amazonaws.com/static.immowissen.org/assets/onboarding/BOTTIMMO-SEPA.pdf"
            class="font-weight-bold"
            >{{ $t('labels.billing-data.billing.sepa-form') }}</a
          >
          {{ $t('labels.billing-data.billing.sepa-download-action') }}
          <div
            class="justify-center py-4 d-flex align-center flex-column flex-md-row justify-md-start"
          >
            <span class="mr-md-1"
              >{{ $t('labels.billing-data.billing.sepa-mandate-reference') }}
            </span>
            <span class="font-weight-bold text-h6"> {{ sepaMandateReference }} </span>
            <div
              class="ml-2 pa-1 copy-btn"
              @click="copyMandateReference"
            >
              <v-icon :alt="$t('labels.billing-data.billing.copy-mandate-reference')"
                >mdi-content-copy</v-icon
              >
            </div>
          </div>
        </div>
        <v-row>
          <v-col
            cols="12"
            md="8"
          >
            <v-file-input
              v-model.lazy="localData.sepaInput"
              variant="outlined"
              accept="image/jpeg,application/pdf"
              :label="$t('labels.billing-data.billing.sepa-upload')"
              :disabled="Boolean(localData.onboardingSepa && localData.onboardingSepa.url)"
              class="px-0 px-md-16"
            />
          </v-col>
          <v-col
            cols="12"
            md="3"
          >
            <v-menu
              v-model="sepaMenu"
              :close-on-content-click="false"
              transition="scale-transition"
            >
              <template v-slot:activator="{ props }">
                <v-text-field
                  v-model="sepaValidAsOf"
                  :label="$t('labels.billing-data.billing.sepa-sign-date')"
                  variant="outlined"
                  readonly
                  v-bind="props"
                />
              </template>
              <v-date-picker
                v-model="sepaValidAsOf"
                :max="getEndDate"
                color="primary"
                @change="sepaMenu = false"
              />
            </v-menu>
          </v-col>
        </v-row>
        <div
          v-if="localData.onboardingSepa && localData.onboardingSepa.url"
          class="truncate"
        >
          {{ $t('labels.billing-data.billing.uploaded-sepa') }}
          <a
            :href="localData.onboardingSepa.url"
            target="_blank"
            rel="noopener noreferrer"
            >{{ localData.onboardingSepa.name }}</a
          >
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import IBAN from 'iban'

import PhoneField from '@/components/PhoneField.vue'
import { ISO_DATE } from '@/lib/date'
import bus, { eventNames } from '@/lib/eventBus'
import { email, empty, length, required, zip } from '@/lib/validation'

import { PAYMENT_METHODS, PAYMENT_METHODS_CH } from './labels'

const VAT_ID =
  /^(ATU[0-9]{8}|DE[0-9]{9})$|^((CHE)(-|\s)?|)\d{3}(\.|\s)?\d{3}(\.|\s)?\d{3}(\s)?(IVA|TVA|MWST|VAT)?$/

const placeholder = {
  DE: {
    iban: 'DE89 3704 0044 0532 0130 00',
    vatId: 'DE123456789',
  },
  AT: {
    iban: 'AT89 3704 0044 0532 0130 00',
    vatId: 'ATU12345678',
  },
  CH: {
    vatId: 'CHE123456789',
  },
}

export default {
  components: {
    PhoneField,
  },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    isOnboarding: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localData: { ...this.data },
      ISO_DATE: ISO_DATE,
      rules: {
        email,
        zip,
        length,
        required,
        vatId: (value) =>
          empty(value) ||
          VAT_ID.test(value) ||
          'Bitte gültige Ust-ID eintragen (DE/AT/CHE + 9 stellige ID)',
        bic: (value) =>
          empty(value) ||
          /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(value) ||
          'Bitte eine valide BIC eintragen',
        iban: (value) => empty(value) || IBAN.isValid(value) || 'Bitte gültige IBAN eintragen',
      },
      sepaMenu: false,
      sepaValidAsOf: this.formatDate(this.data.sepaValidAsOf, ISO_DATE),
      vatIdRequestDateMenu: false,
      vatIdRequestDate: this.formatDate(this.data.vatIdRequestDate, ISO_DATE),
      companyName: this.data.companyName,
      companyNameExceeded: false,
    }
  },
  computed: {
    paymentMethods() {
      return this.localData.companyCountry === 'ch' ? PAYMENT_METHODS_CH : PAYMENT_METHODS
    },
    placeholder() {
      return placeholder[this.localData.companyCountry] || {}
    },
    sepaMandateReference() {
      return 'BOTTIMMO-' + this.localData.customerNumber.padStart(6, '0')
    },
    showCompanyNameAddition() {
      return this.localData.companyNameAddition || this.companyNameExceeded
    },
    getEndDate() {
      var endDate = new Date()
      return endDate.toISOString().slice(0, 10)
    },
    billingAddress() {
      const defaultBillingAddress = {
        city: this.localData.city,
        street: this.localData.street,
        zip: this.localData.zip,
      }
      const companyAddress = {
        city: this.localData.companyCity,
        street: this.localData.companyStreet,
        zip: this.localData.companyZip,
      }
      return this.localData.hasSeparateBillingAddress ? defaultBillingAddress : companyAddress
    },
  },
  watch: {
    companyName(value) {
      if (value.length > 90) {
        this.companyNameExceeded = true
      }
    },
    sepaValidAsOf(value) {
      this.localData.sepaValidAsOf = new Date(value)
    },
    vatIdRequestDate(value) {
      this.localData.vatIdRequestDate = new Date(value)
    },
    data: {
      handler(newValue) {
        this.localData = { ...newValue }
      },
      deep: true,
    },
  },
  methods: {
    updateBillingAddress() {
      this.$emit('updateBillingAddress', this.billingAddress)
    },
    copyMandateReference() {
      navigator.clipboard.writeText(this.sepaMandateReference)
      bus.$emit(eventNames.SHOW_SNACKBAR, { color: 'success', text: 'Mandatsreferenz kopiert' })
    },
    onVatIdInput(value) {
      if (value && String(value).trim()) {
        this.localData.noVatIdAvailable = false
      }
    },
    onVatIdRequestedToggle(value) {
      if (value) {
        this.localData.noVatIdAvailable = false
        this.localData.vatId = ''
        this.localData.taxNumber = ''
      }
    },
    onNoVatIdToggle(value) {
      if (value) {
        this.localData.vatIdRequested = false
        this.localData.vatId = ''
        this.localData.vatIdRequestDate = undefined
      } else {
        this.localData.taxNumber = ''
      }
    },
  },
  emits: ['updateBillingAddress'],
}
</script>

<style scoped>
.copy-btn {
  border-radius: 50px;
}
.copy-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
  cursor: pointer;
}
</style>
